import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { Button, ButtonText, FormField, Spacer, TextInput, Typography } from '@percent/lemonade'
import { useMutation } from '@percent/cause-dashboard/common/hooks'
import { useTranslation } from 'react-i18next'
import styles from './ForgotPassword.module.scss'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useNavigate } from 'react-router-dom'
import { RoutePath } from '../../routes/Routes'
import PaperPlane from '@percent/cause-dashboard/common/assets/images/paper-plane.png'
import { useState } from 'react'
import { emailRegex } from '@percent/utility'
import { CentralCardLayoutWrapper } from '@percent/cause-dashboard/common/components'
import { useForgotPasswordCaptcha } from '@percent/cause-dashboard/common/hooks/useAuthCaptcha/useAuthCaptcha'

export function ForgotPassword() {
  const { authService } = useServices()
  const getCaptchaHeaders = useForgotPasswordCaptcha()
  const [{ success, errorMessage, isLoading }, { apiFunc: forgotPassword }] = useMutation(async (email: string) =>
    authService.forgotPassword(email, await getCaptchaHeaders())
  )
  const [resendSuccess, setResendSuccess] = useState(false)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        email: Yup.string().required('Required').matches(emailRegex, t('errorMessage.validEmail'))
      }),
    validateOnMount: true,
    onSubmit: ({ email }) => {
      forgotPassword(email)
      setResendSuccess(false)
    }
  })

  const { errors, values, touched, handleChange, handleBlur, handleSubmit, isSubmitting, isValid } = formik

  if (success || resendSuccess) {
    return (
      <CentralCardLayoutWrapper
        heading={t('form.checkInbox')}
        CustomHeaderImage={<img src={PaperPlane} alt="paper plane" />}
      >
        <Typography variant="default" className={styles.passwordResetText}>
          {t('form.checkInboxDetails', { email: values.email })}
        </Typography>
        <Spacer size={12} axis="vertical" />
        <Button
          stretch
          size="large"
          type="button"
          data-testid="auth-active-button"
          disabled={resendSuccess}
          onClick={() => {
            forgotPassword(values.email)
            setResendSuccess(true)
          }}
        >
          {t('form.resendEmail')}
        </Button>
      </CentralCardLayoutWrapper>
    )
  }

  return (
    <CentralCardLayoutWrapper heading={t('form.forgotPasswordTitle')} handleSubmit={handleSubmit} error={errorMessage}>
      <Typography className={styles.passwordText} variant="default" data-testid="forgot-password-subtitle">
        {t('form.forgotPasswordSubtitle')}
      </Typography>
      <Spacer size={8} axis="vertical" />
      <FormikProvider value={formik}>
        <FormField
          label={t('form.email')}
          status={touched.email && errors.email ? 'danger' : 'default'}
          statusMessage={errors.email}
          data-testid="email"
          necessity="required"
        >
          <TextInput
            name="email"
            placeholder={t('form.enterEmailAddress')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
          />
        </FormField>
      </FormikProvider>
      <Spacer size={5} axis="vertical" />
      <Button
        stretch
        size="large"
        type="submit"
        data-testid="auth-active-button"
        disabled={isSubmitting || !isValid}
        loading={isLoading}
      >
        {t('button.sendEmail')}
      </Button>
      <div className={styles.passwordFormFooter}>
        <Typography variant="default" className={styles.footerText}>
          {t('typography.lookingForLogin')}
        </Typography>
        <ButtonText onClick={() => navigate(RoutePath.SIGNIN)}>{t('typography.signInHere')}</ButtonText>
      </div>
    </CentralCardLayoutWrapper>
  )
}
